import React, { useEffect, useRef, useState } from 'react';
import DobCalendar from './DobCalendar';

// Source of truth = YYYY-MM-DD (what the parent stores, what Supabase expects).
// Display = dd/mm/yyyy. The icon opens the custom DobCalendar popup.

const formatDisplay = (isoStr) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(isoStr || ''));
  return m ? `${m[3]}/${m[2]}/${m[1]}` : '';
};

// Cross-instance event so opening one calendar closes any other that's open.
const OPEN_EVENT = 'date-input-calendar-open';

export default function DateInputWithCalendar({ name, value, onChange }) {
  const [displayOverride, setDisplayOverride] = useState(null);
  const [calOpen, setCalOpen] = useState(false);
  const anchorRef = useRef(null);
  const instanceIdRef = useRef(Math.random().toString(36).slice(2));

  // When ANY other instance opens, close this one.
  useEffect(() => {
    const handler = (e) => {
      if (e.detail?.id !== instanceIdRef.current) setCalOpen(false);
    };
    window.addEventListener(OPEN_EVENT, handler);
    return () => window.removeEventListener(OPEN_EVENT, handler);
  }, []);

  const openCalendar = () => {
    setCalOpen(v => {
      const next = !v;
      if (next) {
        window.dispatchEvent(new CustomEvent(OPEN_EVENT, {
          detail: { id: instanceIdRef.current },
        }));
      }
      return next;
    });
  };

  // Reset typed-in-progress override whenever the canonical value changes
  // (e.g. teacher reloads the page, parent setState elsewhere).
  React.useEffect(() => {
    setDisplayOverride(null);
  }, [value]);

  const emit = (iso) => onChange?.({ target: { name, value: iso } });

  const handleTextChange = (e) => {
    const digits = e.target.value.replace(/\D/g, '').slice(0, 8);
    let display = digits;
    if (digits.length > 4)      display = `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
    else if (digits.length > 2) display = `${digits.slice(0, 2)}/${digits.slice(2)}`;

    let iso = '';
    if (digits.length === 8) {
      const dd = digits.slice(0, 2);
      const mm = digits.slice(2, 4);
      const yyyy = digits.slice(4);
      const d = new Date(`${yyyy}-${mm}-${dd}`);
      if (!isNaN(d.getTime()) &&
          d.getUTCDate() === parseInt(dd, 10) &&
          d.getUTCMonth() + 1 === parseInt(mm, 10) &&
          d.getUTCFullYear() === parseInt(yyyy, 10)) {
        iso = `${yyyy}-${mm}-${dd}`;
      }
    }
    setDisplayOverride(display);
    emit(iso);
  };

  return (
    <div className="dob-field" ref={anchorRef}>
      <input
        type="text"
        name={name}
        inputMode="numeric"
        placeholder="dd/mm/yyyy"
        maxLength={10}
        value={displayOverride ?? formatDisplay(value)}
        onChange={handleTextChange}
        className="profile-input dob-text-input"
      />
      <span
        role="button"
        tabIndex={0}
        className="dob-calendar-btn"
        title="Open calendar"
        aria-label="Open calendar"
        onMouseDown={(e) => {
          e.preventDefault();
          e.stopPropagation();
          openCalendar();
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            openCalendar();
          }
        }}
      />
      {calOpen && (
        <DobCalendar
          anchorRef={anchorRef}
          value={value || ''}
          onChange={(iso) => {
            setDisplayOverride(formatDisplay(iso));
            emit(iso);
          }}
          onClose={() => setCalOpen(false)}
        />
      )}
    </div>
  );
}
